import { notification } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { QUERY_KEYS } from '@api'
import { DeliveryNoteUtils } from '@utils'

import { DeliveryNotesApi } from './api'

const onError = () => {
  notification.error({
    message: 'Error al obtener los remitos',
  })
}

const uniqueDeliveryNotes = (notes: (string | undefined)[]) =>
  [...new Set(notes.filter(Boolean))] as string[]

interface UseDeliveryNotesOptions {
  currentDeliveryNote?: string
  typedDeliveryNote?: string
}

const useDeliveryNotes = (
  documentNumberId?: string,
  materialSelector?: number,
  _materials?: unknown,
  options?: UseDeliveryNotesOptions,
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const queryKey = [QUERY_KEYS.DELIVERY_NOTES, { documentNumberId, materialId: materialSelector }]

  const { data, isFetching, isSuccess } = useQuery(
    queryKey,
    () => DeliveryNotesApi.getDeliveryNotes(documentNumberId, materialSelector),
    {
      enabled: !!documentNumberId,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always',
      onError,
    },
  )

  const { mutate, isLoading: isSyncingDeliveryNotes } = useMutation(
    () =>
      DeliveryNotesApi.syncDeliveryNotes(documentNumberId as string, materialSelector as number),
    {
      onSuccess: notes => {
        queryClient.setQueryData(queryKey, notes)
        queryClient.invalidateQueries(QUERY_KEYS.MATERIALS)
      },
      onError: () => {
        notification.error({
          message: t('suppliers_router.new_ticket_modal.refresh_delivery_notes_error'),
        })
      },
    },
  )

  const deliveryNotes = useMemo(() => {
    const currentDeliveryNote = DeliveryNoteUtils.normalizeValue(options?.currentDeliveryNote)
    return uniqueDeliveryNotes([...(data ?? []), currentDeliveryNote])
  }, [data, options?.currentDeliveryNote])

  const deliveryNotesOptions = useMemo(
    () =>
      deliveryNotes.map(deliveryNote => ({
        value: deliveryNote,
        label: deliveryNote,
      })),
    [deliveryNotes],
  )

  const typedDeliveryNote = DeliveryNoteUtils.normalizeValue(options?.typedDeliveryNote)
  const typedDeliveryNoteMatchesList =
    !typedDeliveryNote ||
    deliveryNotes.some(
      deliveryNote =>
        deliveryNote === typedDeliveryNote || deliveryNote.startsWith(typedDeliveryNote),
    )

  const showDeliveryNoteNotFoundWarning =
    !!documentNumberId &&
    !isFetching &&
    !isSyncingDeliveryNotes &&
    isSuccess &&
    (deliveryNotes.length === 0 || (!!typedDeliveryNote && !typedDeliveryNoteMatchesList))

  return {
    deliveryNotes,
    deliveryNotesOptions,
    isDeliveryNotesLoading: !!documentNumberId && isFetching && deliveryNotes.length === 0,
    isSyncingDeliveryNotes,
    showDeliveryNoteNotFoundWarning,
    syncDeliveryNotes: () => {
      if (!documentNumberId || !materialSelector || isSyncingDeliveryNotes) return
      mutate()
    },
  }
}

export { DeliveryNoteLabel } from './DeliveryNoteLabel'
export { useDeliveryNotes }
